import { useContext, useMemo } from 'react';

import { getConfirmationStyle, getMixedStyle, getOptionStyle, getOptionStyleNoLabel } from '@libs/EventStylesUtils';
import { isSameDate } from '@libs/NwMoment';
import { checkTenant } from '@libs/NwUtils';

import { ListContext } from '@contexts/ListContext';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const isConfirmed = (event) => {
    if (event.minimal) {
        if (event.Confirmed) {
            return 'confirmed';
        }
        return 'option';
    }
    if (event.Cancel) {
        if (event.ModelsDates) {
            const hasconfirmed = event.ModelsDates.some((md) => md.Models.some((m) => m.OptionNumber === 10));
            const hasoptioned = event.ModelsDates.some((md) => md.Models.some((m) => m.OptionNumber < 10));
            if (hasconfirmed) {
                if (hasoptioned) {
                    return 'mixed';
                } else {
                    return 'confirmed';
                }
            } else {
                return 'option';
            }
        }
        if (event.EventDates) {
            const hasconfirmed = event.EventDates.some((ed) => ed.EventDatesModels.some((m) => m.OptionNumber === 10));
            const hasoptioned = event.EventDates.some((ed) => ed.EventDatesModels.some((m) => m.OptionNumber < 10));
            if (hasconfirmed) {
                if (hasoptioned) {
                    return 'mixed';
                } else {
                    return 'confirmed';
                }
            } else {
                return 'option';
            }
        }
    } else {
        switch (event.Confirmed) {
            case 1:
                return 'mixed';
            case 2:
                return 'confirmed';
            default:
                return 'option';
        }
    }
};

const isConfirmedForDate = (event, date) => {
    if (event.minimal) {
        if (event.Confirmed) {
            return 'confirmed';
        }
        return 'option';
    }
    if (event.ModelsDates) {
        const eventdate = event.ModelsDates.find((md) => isSameDate(md.FromDate, date));
        if (eventdate) {
            const hasconfirmed = eventdate.Models.some((m) => m.OptionNumber === 10);
            const hasoptioned = eventdate.Models.some((m) => m.OptionNumber < 10);
            if (hasconfirmed) {
                if (hasoptioned) {
                    return 'mixed';
                } else {
                    return 'confirmed';
                }
            } else {
                return 'option';
            }
        } else {
            return isConfirmed(event);
        }
    }
    if (event.EventDates) {
        const eventdate = event.EventDates.find((ed) => isSameDate(ed.FromDate, date));
        if (eventdate) {
            const hasconfirmed = eventdate.EventDatesModels.some((m) => m.OptionNumber === 10);
            const hasoptioned = eventdate.EventDatesModels.some((m) => m.OptionNumber < 10);
            if (hasconfirmed) {
                if (hasoptioned) {
                    return 'mixed';
                } else {
                    return 'confirmed';
                }
            } else {
                return 'option';
            }
        } else {
            return isConfirmed(event);
        }
    }
};

const isConfirmedForModel = (event, modelID) => {
    if (event.minimal) {
        if (event.Confirmed) {
            return 'confirmed';
        }
        return 'option';
    }
    if (event.ModelsDates) {
        const hasconfirmed = event.ModelsDates.some((md) =>
            md.Models.some((m) => m.OptionNumber === 10 && m.ModelID === modelID)
        );
        const hasoptioned = event.ModelsDates.some((md) =>
            md.Models.some((m) => m.OptionNumber < 10 && m.ModelID === modelID)
        );
        if (hasconfirmed) {
            if (hasoptioned) {
                return 'mixed';
            } else {
                return 'confirmed';
            }
        } else {
            return 'option';
        }
    }
    if (event.EventDates) {
        const hasconfirmed = event.EventDates.some((md) =>
            md.EventDatesModels.some((m) => m.OptionNumber === 10 && m.ModelID === modelID)
        );
        const hasoptioned = event.EventDates.some((md) =>
            md.EventDatesModels.some((m) => m.OptionNumber < 10 && m.ModelID === modelID)
        );
        if (hasconfirmed) {
            if (hasoptioned) {
                return 'mixed';
            } else {
                return 'confirmed';
            }
        } else {
            return 'option';
        }
    }
};

const isConfirmedForModelAndDate = (event, modelID, date) => {
    if (event.minimal) {
        if (event.Confirmed) {
            return 'confirmed';
        }
        return 'option';
    }
    if (event.ModelsDates) {
        const eventdate = event.ModelsDates.find((md) => isSameDate(md.FromDate, date));
        if (eventdate) {
            const isconfirmed = eventdate.Models.some((m) => m.OptionNumber === 10 && m.ModelID === modelID);
            if (isconfirmed) {
                return 'confirmed';
            }
            return 'option';
        } else {
            return isConfirmedForModel(event, modelID);
        }
    }
    if (event.EventDates) {
        const eventdate = event.EventDates.find((ed) => isSameDate(ed.FromDate, date));
        if (eventdate) {
            const isconfirmed = eventdate.EventDatesModels.some((m) => m.OptionNumber === 10 && m.ModelID === modelID);
            if (isconfirmed) {
                return 'confirmed';
            }
            return 'option';
        } else {
            return isConfirmedForModel(event, modelID);
        }
    }
};

//this method can receive an event in 3 different formats (full details, single models details or minimal - in transaction details)
const useEventStyle = (eventdata, evtype, forModel, forDate, isMinimal) => {
    const event = { ...eventdata, minimal: isMinimal };

    const { cachedList } = useContext(ListContext);
    const typeName =
        event &&
        event.TypeName &&
        event.TypeName.toLowerCase() === 'accommodation' &&
        ((event.SubTypeCode === '1' && event.SubTypeCode) || event.RoomID)
            ? 'RoomLodging'
            : event.TypeName;

    const eventtype = useMemo(() => {
        let eventtype = null;
        if (typeName) {
            eventtype = cachedList.eventTypes.items.find((et) => et.code.toLowerCase() === typeName.toLowerCase());
        } else {
            if (evtype) {
                eventtype = cachedList.eventTypes.items.find((et) => et.code.toLowerCase() === evtype.toLowerCase());
            }
        }
        return eventtype;
    }, [typeName, evtype, cachedList.eventTypes.items, cachedList.lastUpdate]);

    const greenTravels = checkTenant(['elitemodels', 'dmanagement'], false);
    const yellowExternalJobs = checkTenant(['dmanagement'], false);
    const pinkExternalJobs = checkTenant(['wave'], false);

    let bgcolor = eventtype.baseColor;
    let color = eventtype.textColor;
    let icon = eventtype.icon;
    let name = eventtype.name;

    let mixcolors = null;
    let confcolors = null;
    let optcolors = null;
    let confirmationStatus = '';

    let ext_mixcolors = null;
    let ext_confcolors = null;
    let ext_optcolors = null;
    let ext_confirmationStatus = '';

    switch (eventtype.code.toLowerCase()) {
        case 'casting':
            if (event.SubTypeCode) {
                const st = eventtype.subTypes.find((st) => {
                    return st.Code.toLowerCase() === event.SubTypeCode.toLowerCase();
                });
                if (st) {
                    name = st.Name;
                    bgcolor = `#${st.BackColor}`;
                    color = `#${st.ForeColor}`;
                }
            } else {
                if (eventtype.subTypes && eventtype.subTypes.length > 0) {
                    const st = eventtype.subTypes.find((st) => st.Default);
                    if (st) {
                        name = st.Name;
                        bgcolor = `#${st.BackColor}`;
                        color = `#${st.ForeColor}`;
                    }
                }
            }
            break;
        case 'job':
            if (event.SubTypeCode) {
                const st = eventtype.subTypes.find((st) => {
                    return st.Code.toLowerCase() === event.SubTypeCode.toLowerCase();
                });
                if (st) {
                    mixcolors = getMixedStyle(st);
                    confcolors = getConfirmationStyle(st);
                    optcolors = getOptionStyle(st, event.OptionNumber);
                }
            } else {
                if (eventtype.subTypes && eventtype.subTypes.length > 0) {
                    const st = eventtype.subTypes.find((st) => st.Default);
                    if (st) {
                        mixcolors = getMixedStyle(st);
                        confcolors = getConfirmationStyle(st);
                        optcolors = getOptionStyle(st, event.OptionNumber);
                    }
                } else {
                    confcolors = { bgcolor: bgcolor, name: name, color: color };
                    mixcolors = { ...confcolors };
                    optcolors = getOptionStyleNoLabel(eventtype);
                }
            }

            if (event.OptionNumber) {
                if (event.OptionNumber === 10) {
                    confirmationStatus = 'confirmed';
                } else {
                    confirmationStatus = 'option';
                }
            } else {
                if (forDate) {
                    if (forModel) {
                        confirmationStatus = isConfirmedForModelAndDate(event, forModel, forDate);
                    } else {
                        confirmationStatus = isConfirmedForDate(event, forDate);
                    }
                } else {
                    if (forModel) {
                        confirmationStatus = isConfirmedForModel(event, forModel);
                    } else {
                        confirmationStatus = isConfirmed(event);
                    }
                }
            }
            switch (confirmationStatus) {
                case 'confirmed':
                    bgcolor = confcolors.bgcolor;
                    color = confcolors.color;
                    name = confcolors.name;
                    break;
                case 'mixed':
                    bgcolor = mixcolors.bgcolor;
                    color = mixcolors.color;
                    name = mixcolors.name;
                    break;
                case 'option':
                    bgcolor = optcolors.bgcolor;
                    color = optcolors.color;
                    name = optcolors.name;
                    break;
                default:
                    break;
            }
            break;
        case 'ext':
            if (event.SubTypeCode) {
                const st = eventtype.subTypes.find((st) => {
                    return st.Code.toLowerCase() === event.SubTypeCode.toLowerCase();
                });
                if (st) {
                    ext_mixcolors = getMixedStyle(st);
                    ext_confcolors = getConfirmationStyle(st);
                    ext_optcolors = getOptionStyle(st, event.OptionNumber);
                }
            } else {
                if (eventtype.subTypes && eventtype.subTypes.length > 0) {
                    const st = eventtype.subTypes.find((st) => st.Default);
                    if (st) {
                        ext_mixcolors = getMixedStyle(st);
                        ext_confcolors = getConfirmationStyle(st);
                        ext_optcolors = getOptionStyle(st, event.OptionNumber);
                    }
                } else {
                    ext_confcolors = { bgcolor: bgcolor, name: name, color: color };
                    ext_mixcolors = { ...ext_confcolors };
                    ext_optcolors = getOptionStyleNoLabel(eventtype);
                }
            }

            if (event.OptionNumber) {
                if (event.OptionNumber === 10) {
                    ext_confirmationStatus = 'confirmed';
                } else {
                    ext_confirmationStatus = 'option';
                }
            } else {
                if (forDate) {
                    if (forModel) {
                        ext_confirmationStatus = isConfirmedForModelAndDate(event, forModel, forDate);
                    } else {
                        ext_confirmationStatus = isConfirmedForDate(event, forDate);
                    }
                } else {
                    if (forModel) {
                        ext_confirmationStatus = isConfirmedForModel(event, forModel);
                    } else {
                        ext_confirmationStatus = isConfirmed(event);
                    }
                }
            }
            switch (ext_confirmationStatus) {
                case 'confirmed':
                    bgcolor = ext_confcolors.bgcolor;
                    color = ext_confcolors.color;
                    name = ext_confcolors.name;
                    break;
                case 'mixed':
                    bgcolor = ext_mixcolors.bgcolor;
                    color = ext_mixcolors.color;
                    name = ext_mixcolors.name;
                    break;
                case 'option':
                    bgcolor = ext_optcolors.bgcolor;
                    color = ext_optcolors.color;
                    name = ext_optcolors.name;
                    break;
                default:
                    break;
            }
            break;
        case 'todo':
            if (event.Completed && eventtype.secondaryColor) {
                bgcolor = eventtype.secondaryColor;
            }
            if (event.Completed && eventtype.secondaryTextColor) {
                color = eventtype.secondaryTextColor;
            }
            if (event.Completed && eventtype.secondaryIcon) {
                icon = eventtype.secondaryIcon;
            }
            break;
        case 'followup':
            if (event.SubTypeCode) {
                const followupLabel = cachedList.followUpLabels.items.find((item) => item.Code === event.SubTypeCode);
                if (followupLabel) {
                    bgcolor = followupLabel.BackColor;
                    color = followupLabel.ForeColor;
                    if (followupLabel.ModelNotAvailable) {
                        icon = byPrefixAndName.fass['do-not-enter'];
                    }
                    name = followupLabel.Label;
                }
            }
            break;
        case 'extjob':
            if (yellowExternalJobs) {
                bgcolor = '#ecd622';
                color = '#000000';
                if (event.Confirmed === 0) {
                    //lighter yellow
                    bgcolor = '#f0eb91';
                }
            } else if (pinkExternalJobs) {
                bgcolor = '#ffc0cb';
                color = '#000000';
                if (event.Confirmed === 0) {
                    //lighter pink
                    bgcolor = '#ffe4e1';
                }
            } else {
                if (event.Confirmed === 0 && eventtype.secondaryColor) {
                    bgcolor = eventtype.secondaryColor;
                }
                if (event.Confirmed === 0 && eventtype.secondaryTextColor) {
                    color = eventtype.secondaryTextColor;
                }
            }
            if (event.Confirmed === 0 && eventtype.secondaryIcon) {
                icon = eventtype.secondaryIcon;
            }
            break;

        case 'travel':
        case 'accommodation':
        case 'roomlodging':
            if (greenTravels) {
                bgcolor = '#8FBC8F';
                color = '#ffffff';
            }
            break;
        default:
            break;
    }
    return { color: color, backgroundColor: bgcolor, icon: icon, name: name };
};

export default useEventStyle;
